import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ChevronRight, Package, PackageCheck, Thermometer, Truck, User } from "lucide-react";
import {
  fetchDeliveredOrders,
  fetchDriverOrders,
  type DeliveredOrder,
  type DriverOrder,
} from "@/lib/driverApi";
import { getApiErrorMessage } from "@/lib/api";
import { formatDeliveredAt, formatPtDate, todayBusinessDate } from "@/lib/deliveryDate";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { DateNavigator } from "@/components/driver/DateNavigator";
import { EmptyState, ErrorBanner, InfoCell, Loading } from "@/components/driver/DeliveryFeedback";

type Tab = "rota" | "entregues";

export default function DriverOrders() {
  const today = useMemo(() => todayBusinessDate(), []);
  const [date, setDate] = useState<string>(today);
  const [tab, setTab] = useState<Tab>("rota");

  const [active, setActive] = useState<DriverOrder[] | null>(null);
  const [activeError, setActiveError] = useState<string | null>(null);
  const [delivered, setDelivered] = useState<DeliveredOrder[] | null>(null);
  const [deliveredError, setDeliveredError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setActive(null);
    setActiveError(null);
    fetchDriverOrders(date)
      .then((list) => alive && setActive(list))
      .catch((err) => alive && setActiveError(getApiErrorMessage(err)));
    return () => {
      alive = false;
    };
  }, [date]);

  useEffect(() => {
    let alive = true;
    setDelivered(null);
    setDeliveredError(null);
    fetchDeliveredOrders(date)
      .then((list) => alive && setDelivered(list))
      .catch((err) => alive && setDeliveredError(getApiErrorMessage(err)));
    return () => {
      alive = false;
    };
  }, [date]);

  const isToday = date === today;

  return (
    <div className="space-y-4 pb-6">
      <header>
        <h1 className="text-xl font-semibold">Minhas entregas</h1>
      </header>

      <div className="sticky top-2 z-10 rounded-xl border bg-card/95 p-1.5 shadow-sm backdrop-blur">
        <DateNavigator value={date} today={today} onChange={setDate} />
      </div>

      <Tabs value={tab} onValueChange={(value) => setTab(value as Tab)}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="rota" className="gap-2">
            <Truck className="h-4 w-4" />
            Em rota
            {active && active.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                {active.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="entregues" className="gap-2">
            <PackageCheck className="h-4 w-4" />
            Entregues
            {delivered && delivered.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                {delivered.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="rota" className="space-y-3 pt-4">
          {activeError ? (
            <ErrorBanner message={activeError} />
          ) : !active ? (
            <Loading label="Carregando entregas..." />
          ) : active.length === 0 ? (
            <EmptyState icon={Truck} message="Sem entregas em rota nesta data." />
          ) : (
            active.map((order) => <RouteCard key={order.id} order={order} />)
          )}
        </TabsContent>

        <TabsContent value="entregues" className="space-y-3 pt-4">
          {deliveredError ? (
            <ErrorBanner message={deliveredError} />
          ) : !delivered ? (
            <Loading label="Carregando entregas..." />
          ) : delivered.length === 0 ? (
            <EmptyState
              icon={PackageCheck}
              message={isToday ? "Nenhuma entrega registrada hoje." : "Nenhuma entrega registrada nesta data."}
            />
          ) : (
            delivered.map((entry) => <DeliveredCard key={entry.orderId} delivered={entry} />)
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function RouteCard({ order }: { order: DriverOrder }) {
  return (
    <Link to={`/entregador/pedidos/${order.id}`} className="block focus:outline-none">
      <Card className="group relative overflow-hidden border-border/60 transition-all hover:border-primary hover:shadow-md active:scale-[0.99]">
        <span className="absolute inset-y-0 left-0 w-1 bg-primary/70 transition-colors group-hover:bg-primary" />
        <CardContent className="flex items-center gap-3 p-4">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
            <Package className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <div className="truncate font-semibold leading-tight">{order.clientName}</div>
            <div className="text-xs text-muted-foreground">
              {order.deliveryWindow || (order.deliveryDate ? formatPtDate(order.deliveryDate) : null)}
            </div>
            {order.notes && <p className="line-clamp-1 text-xs text-muted-foreground">{order.notes}</p>}
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
        </CardContent>
      </Card>
    </Link>
  );
}

function DeliveredCard({ delivered }: { delivered: DeliveredOrder }) {
  return (
    <Card className="overflow-hidden border-emerald-200/60 bg-emerald-50/40 dark:bg-emerald-950/20">
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-emerald-500/15 text-emerald-600">
            <CheckCircle2 className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1 space-y-0.5">
            <div className="truncate font-semibold leading-tight">{delivered.clientName}</div>
            <div className="text-xs text-muted-foreground">
              Entregue às{" "}
              <span className="font-medium text-foreground">{formatDeliveredAt(delivered.deliveredAt)}</span>
            </div>
          </div>
          <Badge className="border-0 bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-300">
            NF {delivered.nfNumber}
          </Badge>
        </div>

        <div className="grid gap-2 text-xs sm:grid-cols-2">
          <InfoCell icon={Thermometer} label="Temperatura">
            {delivered.truckTemperature} °C
          </InfoCell>
          <InfoCell icon={User} label="Conferente">
            {delivered.receiverName}
          </InfoCell>
        </div>
      </CardContent>
    </Card>
  );
}
